// Shared file/download helpers — kept feature-agnostic so generic components (e.g. LightboxModal) don't import from a feature folder.

// Last path segment, decoded for display. decodeURIComponent throws on a bare "%" — fall back to the raw segment.
export function filenameFromUrl(uri: string, fallback = 'image.png'): string {
  // Drop query/hash first so presigned URLs don't leak their signature into the name
  const segment = uri.split(/[?#]/)[0].split('/').pop();
  if (!segment) return fallback;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// A filesystem-safe, lowercase slug for a user-typed name: accents folded ("Rivière" → "riviere"), everything but letters
// and digits collapsed to single dashes. Falls back when nothing printable is left.
export function toFileSlug(name: string | null | undefined, fallback = 'file'): string {
  const slug = name
    ?.trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

// Save an in-memory string as a file via a temporary object URL.
export function downloadTextFile(content: string, filename: string, mimeType: string): void {
  saveBlob(new Blob([content], { type: mimeType }), filename);
}

// Save a blob under the given name via a temporary object URL.
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Fetch-then-blob so the file saves under its own name instead of navigating to the CDN URL.
// Returns false instead of throwing so callers can surface a failure (CORS/404) next to the button.
export async function downloadFileFromUrl(url: string, filename: string): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed with ${response.status}`);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    return true;
  } catch (error) {
    console.error('Failed to download file:', error);
    return false;
  }
}
